import { useState, useCallback, useEffect } from 'react';
import { toast } from '../components/Toast';
import { listMyInquiries } from '../api/inquiry.api';
import EmptyState from '../components/EmptyState';
import InquiryChat from '../components/InquiryChat';

const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'New', value: 'new' },
    { label: 'In Discussion', value: 'in_discussion' },
    { label: 'Quoted', value: 'quoted' },
    { label: 'Accepted', value: 'accepted' },
    { label: 'Closed', value: 'closed_converted' },
    { label: 'Rejected', value: 'closed_rejected' },
];

const STATUS_CONFIG = {
    new:              { label: 'New',           cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
    in_discussion:    { label: 'In Discussion', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
    quoted:           { label: 'Quoted',        cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
    accepted:         { label: 'Accepted',      cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
    closed_converted: { label: 'Converted',     cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
    closed_rejected:  { label: 'Rejected',      cls: 'bg-muted text-muted-foreground' },
};

function StatusBadge({ status }) {
    const cfg = STATUS_CONFIG[status] || { label: status, cls: 'bg-muted text-muted-foreground' };
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
            {cfg.label}
        </span>
    );
}

const formatKES = (v) => 'KES ' + Number(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

export default function BulkInquiries() {
    const navigate = useNavigate();
    const [inquiries, setInquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [chatInquiry, setChatInquiry] = useState(null);

    useEffect(() => {
        document.title = 'My Bulk Inquiries | Fone Express';
    }, []);

    const fetchInquiries = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: 10 };
            if (activeTab) params.status = activeTab;
            const res = await listMyInquiries(params);
            if (res.data?.isOk) {
                setInquiries(res.data.data || []);
                setTotalPages(res.data.meta?.totalPages || 1);
            } else {
                setInquiries([]);
            }
        } catch {
            setError('Unable to load inquiries. Please try again.');
            toast.error('Failed to load bulk inquiries');
        } finally {
            setLoading(false);
        }
    }, [page, activeTab]);

    useEffect(() => {
        fetchInquiries();
    }, [fetchInquiries]);

    const handleTabChange = (value) => {
        setActiveTab(value);
        setPage(1);
    };

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground">Bulk Inquiries</h1>
                <p className="text-muted-foreground text-sm mt-1">Track your corporate and bulk order requests</p>
            </div>

            {/* Status filter tabs */}
            <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-4 overflow-x-auto">
                {STATUS_TABS.map((tab) => (
                    <button
                        key={tab.value}
                        onClick={() => handleTabChange(tab.value)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                            activeTab === tab.value
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground hover:bg-border'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Loading skeleton */}
            {loading && (
                <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="bg-card border border-border rounded-lg p-4 animate-pulse">
                            <div className="flex justify-between items-start mb-3">
                                <div className="space-y-2">
                                    <div className="h-4 bg-muted rounded w-36"></div>
                                    <div className="h-3 bg-muted rounded w-24"></div>
                                </div>
                                <div className="h-6 bg-muted rounded-full w-24"></div>
                            </div>
                            <div className="h-3 bg-muted rounded w-48 mb-4"></div>
                            <div className="flex justify-between items-center">
                                <div className="h-3 bg-muted rounded w-20"></div>
                                <div className="h-8 bg-muted rounded w-28"></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Error state */}
            {!loading && error && (
                <div className="text-center py-12">
                    <i className="ri-error-warning-line text-4xl text-red-400 mb-4 block"></i>
                    <p className="text-muted-foreground mb-4">{error}</p>
                    <button
                        onClick={fetchInquiries}
                        className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            )}

            {/* Empty state */}
            {!loading && !error && inquiries.length === 0 && (
                <EmptyState
                    icon="ri-file-list-3-line"
                    title="No inquiries yet"
                    description={activeTab ? 'No inquiries found with this status.' : "You haven't submitted any bulk or corporate inquiries yet."}
                    actionLabel="Submit an Inquiry"
                    actionLink="/bulk-orders"
                />
            )}

            {/* Inquiry list */}
            {!loading && !error && inquiries.length > 0 && (
                <div className="space-y-4">
                    {inquiries.map((inq) => (
                        <div
                            key={inq._id || inq.inquiryNumber}
                            className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                            {/* Header row */}
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                                <div>
                                    <p className="font-semibold text-foreground">
                                        Inquiry #{inq.inquiryNumber}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {new Date(inq.createdAt).toLocaleDateString('en-KE', {
                                            year: 'numeric', month: 'short', day: 'numeric',
                                        })}
                                    </p>
                                </div>
                                <StatusBadge status={inq.status} />
                            </div>

                            {/* Company */}
                            <p className="text-sm text-muted-foreground mb-3 flex items-center gap-1.5">
                                <i className="ri-building-line text-base"></i>
                                {inq.companyName}
                            </p>

                            {/* Items thumbnails */}
                            {inq.items?.length > 0 && (
                                <div className="flex items-center gap-2 mb-3">
                                    {inq.items.slice(0, 4).map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="w-12 h-12 border border-border rounded-lg bg-muted flex-shrink-0 overflow-hidden"
                                        >
                                            {item.imageUrl ? (
                                                <img
                                                    src={item.imageUrl}
                                                    alt={item.productName}
                                                    className="w-full h-full object-contain p-1"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <i className="ri-box-3-line text-muted-foreground/40"></i>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {inq.items.length > 4 && (
                                        <span className="text-xs text-muted-foreground font-medium">
                                            +{inq.items.length - 4} more
                                        </span>
                                    )}
                                    <span className="text-xs text-muted-foreground ml-1">
                                        {inq.items.length} item{inq.items.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            )}

                            {/* Footer row */}
                            <div className="flex items-center justify-between pt-3 border-t border-border">
                                <div>
                                    {inq.quote?.grandTotal > 0 ? (
                                        <p className="text-sm font-semibold text-foreground">
                                            Quote: {formatKES(inq.quote.grandTotal)}
                                        </p>
                                    ) : (
                                        <p className="text-xs text-muted-foreground">Awaiting quote</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setChatInquiry(inq)}
                                        className="flex items-center gap-1.5 px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary text-sm font-medium rounded-lg transition-colors"
                                    >
                                        <i className="ri-chat-3-line text-sm"></i>
                                        Chat
                                    </button>
                                    <button
                                        onClick={() => navigate(`/account/bulk-inquiries/${inq.inquiryNumber}`)}
                                        className="flex items-center gap-1.5 px-4 py-2 bg-muted hover:bg-border text-foreground text-sm font-medium rounded-lg transition-colors"
                                    >
                                        View Details
                                        <i className="ri-arrow-right-line text-sm"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-4 py-2 border border-border rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-muted-foreground">
                        Page {page} of {totalPages}
                    </span>
                    <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="px-4 py-2 border border-border rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Chat modal */}
            {chatInquiry && (
                <div
                    className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
                    onClick={() => setChatInquiry(null)}
                >
                    <div
                        className="bg-card w-full sm:max-w-lg h-[85vh] sm:h-[600px] sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-border">
                            <div>
                                <p className="font-semibold text-foreground text-sm">Inquiry #{chatInquiry.inquiryNumber}</p>
                                <p className="text-xs text-muted-foreground">{chatInquiry.companyName}</p>
                            </div>
                            <button
                                onClick={() => setChatInquiry(null)}
                                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground transition-colors"
                            >
                                <i className="ri-close-line text-lg"></i>
                            </button>
                        </div>
                        {/* Chat body */}
                        <div className="flex-1 min-h-0">
                            <InquiryChat
                                inquiryNumber={chatInquiry.inquiryNumber}
                                isClosed={['closed_converted', 'closed_rejected'].includes(chatInquiry.status)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
